import { getLateFeeAmount, isPetLateFeesWaived } from 'src/components/common/calculatePetFee';

export const PET_LICENSE_CART_KEY_PREFIX = 'petLicenseCheckoutCart';

export function getPetLicenseCartStorageKey (
  townshipId: string | number | undefined,
  userId: string | number | undefined
): string {
  return `${PET_LICENSE_CART_KEY_PREFIX}_${townshipId ?? '0'}_${userId ?? '0'}`;
}

/** Snapshot stored in localStorage for checkout (fees recomputed server-side). */
export interface PetLicenseCartItem {
  ApplicationID: number;
  ApplicationNumber?: string;
  PetName?: string;
  PetType?: string;
  OwnerName?: string;
  UserID?: number;
  IsSpayedNeutered?: unknown;
  ApplicationDate?: string;
  CreateDate?: string;
  StateRegistrationFee?: number;
  PilotClinicFee?: number;
  LateFeesWaived?: unknown;
}

export function readPetLicenseCart (key: string): PetLicenseCartItem[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as PetLicenseCartItem[]) : [];
  } catch {
    return [];
  }
}

export function writePetLicenseCart (key: string, items: PetLicenseCartItem[]): void {
  localStorage.setItem(key, JSON.stringify(items));
}

export function addPetLicenseCartItem (
  key: string,
  item: PetLicenseCartItem
): PetLicenseCartItem[] {
  const cur = readPetLicenseCart(key);
  if (cur.some((x) => x.ApplicationID === item.ApplicationID)) {
    return cur;
  }
  const next = [...cur, item];
  writePetLicenseCart(key, next);
  return next;
}

export function removePetLicenseCartItem (
  key: string,
  applicationId: number
): PetLicenseCartItem[] {
  const next = readPetLicenseCart(key).filter((x) => x.ApplicationID !== applicationId);
  writePetLicenseCart(key, next);
  return next;
}

export function clearPetLicenseCart (key: string): void {
  localStorage.removeItem(key);
}

/** Line breakdown before card surcharge (matches backend getPetFeeBreakdown). */
export interface PetLicenseLineFeeBreakdown {
  licenseFee: number;
  stateRegistration: number;
  pilotClinic: number;
  animalPopulationControl: number;
  subtotal: number;
  lateFee: number;
  lateFeesWaived: boolean;
  totalBeforeTax: number;
}

export function computePetCartLineFeeBreakdown (
  item: PetLicenseCartItem,
  feeObject: Record<string, number>
): PetLicenseLineFeeBreakdown {
  const applicationFeeSpayed = Number(feeObject?.ApplicationFeeSpayed) || 0;
  const applicationFeeNonSpayed = Number(feeObject?.ApplicationFeeNonSpayed) || 0;
  const stateRegistration =
    Number(item.StateRegistrationFee) ||
    Number(feeObject?.StateRegistrationFeePet) ||
    0;
  const pilotClinic =
    Number(item.PilotClinicFee) || Number(feeObject?.PilotClinicFeePet) || 0;
  const lateMonthlyFee = Number(feeObject?.LateMonthlyFee) || 0;
  const delinquencyFee = Number(feeObject?.DelinquencyFee) || 0;

  const licenseFee = Math.max(0, applicationFeeSpayed - stateRegistration - pilotClinic);
  const isSpayed =
    item.IsSpayedNeutered === true ||
    item.IsSpayedNeutered === 1 ||
    item.IsSpayedNeutered === '1' ||
    item.IsSpayedNeutered === 'Yes' ||
    item.IsSpayedNeutered === 'yes';
  const animalPopulationControl = isSpayed
    ? 0
    : Math.max(0, applicationFeeNonSpayed - applicationFeeSpayed);
  const subtotal = licenseFee + stateRegistration + pilotClinic + animalPopulationControl;

  const lateFeesWaived = isPetLateFeesWaived(item.LateFeesWaived);
  const applicationDate = item.ApplicationDate
    ? new Date(item.ApplicationDate)
    : item.CreateDate
      ? new Date(item.CreateDate)
      : new Date();
  const lateFee = lateFeesWaived
    ? 0
    : getLateFeeAmount(subtotal, lateMonthlyFee, delinquencyFee, applicationDate, isSpayed);

  const totalBeforeTax = subtotal + lateFee;
  return {
    licenseFee,
    stateRegistration,
    pilotClinic,
    animalPopulationControl,
    subtotal,
    lateFee,
    lateFeesWaived,
    totalBeforeTax
  };
}

/** Per-line total before credit card surcharge (matches backend getPetFeeBreakdown total). */
export function computePetCartLineTotalBeforeTax (
  item: PetLicenseCartItem,
  feeObject: Record<string, number>
): number {
  return computePetCartLineFeeBreakdown(item, feeObject).totalBeforeTax;
}

export function computePetCartMoneyTotals (
  items: PetLicenseCartItem[],
  feeObject: Record<string, number>
): { combinedBeforeTax: number; taxPercent: number; totalDue: number } {
  const taxPercent = Number(feeObject?.CreditCardFee) || 0;
  let combinedBeforeTax = 0;
  for (const it of items) {
    combinedBeforeTax += computePetCartLineTotalBeforeTax(it, feeObject);
  }
  const taxAmount = combinedBeforeTax * (taxPercent / 100);
  const totalDue = combinedBeforeTax + taxAmount;
  return { combinedBeforeTax, taxPercent, totalDue };
}
